import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../../input/input.component';
import { ValidationMessageComponent } from '../../validation-message/validation-message.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

export interface LinkData {
  displayName: string;
  url: string;
}

function urlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value?.trim();
  if (!value) {
    return null;
  }

  try {
    new URL(value);
    return null;
  } catch {
    return { invalidUrl: { message: 'Invalid URL format' } };
  }
}

@Component({
  selector: 'lib-link-input',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    ValidationMessageComponent,
    TranslocoPipe,
  ],
  templateUrl: './link-input.component.html',
  styleUrl: './link-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkInputComponent {
  private readonly translocoService = inject(TranslocoService);
  readonly currentLang = this.translocoService.getActiveLang();

  initialDisplayName = input<string>('');
  initialUrl = input<string>('');

  linkInsert = output<LinkData>();
  linkCancel = output<void>();

  linkForm = new FormGroup({
    displayName: new FormControl('', Validators.required),
    url: new FormControl('', [Validators.required, urlValidator]),
  });

  isFormValid = computed(() => this.linkForm.valid);

  insert(): void {
    if (this.linkForm.valid) {
      const { displayName, url } = this.linkForm.value as LinkData;
      this.linkInsert.emit({
        displayName: displayName.trim(),
        url: url.trim(),
      });
      this.reset();
    }
  }

  cancel(): void {
    this.linkCancel.emit();
    this.reset();
  }

  reset(): void {
    this.linkForm.reset({ displayName: '', url: '' });
  }

  focusDisplayName(): void {
    const inputContainer = document.getElementById('linkDisplayName');
    const input = inputContainer?.querySelector('input');
    input?.focus();
  }

  setInitialValues(displayName: string, url: string): void {
    this.linkForm.patchValue({ displayName, url });
  }
}
